import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import CiPaginationBar from './CiPaginationBar';

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
  } catch {
    return String(iso);
  }
}

function SortTh({ label, colKey, sortKey, sortDir, onSort }) {
  const ind = sortKey === colKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th scope="col">
      <button
        type="button"
        className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
        onClick={() => onSort(colKey)}
      >
        {label}
        {ind}
      </button>
    </th>
  );
}

const MAIN_COLUMNS = [
  { colKey: 'address_line1', label: 'Address' },
  { colKey: 'city', label: 'City' },
  { colKey: 'customer_name', label: 'Customer' },
];

function customerDetailLinkSearch(querySuffix) {
  const p = new URLSearchParams(String(querySuffix || '').replace(/^\?/, ''));
  p.set('tab', 'addresses');
  p.delete('customer_id');
  const s = p.toString();
  return s ? `?${s}` : '?tab=addresses';
}

export default function ShipToDirectoryTable({
  basePath,
  querySuffix,
  rows,
  loading,
  total,
  page,
  totalPages,
  showSiteColumn,
  sortKey,
  sortDir,
  onSort,
  onPrevPage,
  onNextPage,
}) {
  const colSpan = (showSiteColumn ? 7 : 6);
  const customerLinkSuffix = useMemo(
    () => customerDetailLinkSearch(querySuffix),
    [querySuffix],
  );

  return (
    <div className="ci-card ci-card-flush">
      <CiPaginationBar
        position="top"
        page={page}
        totalPages={totalPages}
        total={total}
        loading={loading}
        onPrevPage={onPrevPage}
        onNextPage={onNextPage}
      />
      <div className="table-responsive">
        <table className="table m-0">
          <thead>
            <tr>
              {MAIN_COLUMNS.map((c) => (
                <SortTh
                  key={c.colKey}
                  label={c.label}
                  colKey={c.colKey}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              ))}
              <th scope="col">State / Postal</th>
              {showSiteColumn ? (
                <SortTh
                  label="Site"
                  colKey="site_name"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              ) : null}
              <th scope="col">Default</th>
              <SortTh
                label="Created"
                colKey="created_at"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-4">
                  No ship-to addresses found.
                </td>
              </tr>
            ) : null}
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-4">
                  Loading…
                </td>
              </tr>
            ) : null}
            {!loading &&
              rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="fw-semibold">{r.address_line1 || '—'}</div>
                    {r.address_line2 ? <div className="small text-muted">{r.address_line2}</div> : null}
                    {r.label ? <div className="small text-muted">{r.label}</div> : null}
                  </td>
                  <td>{r.city || '—'}</td>
                  <td>
                    {r.customer_id ? (
                      <Link
                        to={`${basePath}/customers/${r.customer_id}${customerLinkSuffix}`}
                        className="text-decoration-none"
                      >
                        <span className="fw-semibold text-body">
                          {String(r.customer_name || '').trim() || '—'}
                        </span>
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="small text-muted">
                    {[r.state_province, r.postal_code].filter(Boolean).join(' ') || '—'}
                  </td>
                  {showSiteColumn ? (
                    <td className="small text-muted">{r.site_name || '—'}</td>
                  ) : null}
                  <td>
                    {r.is_default_shipping ? <span className="badge bg-primary me-1">Ship</span> : null}
                    {r.is_default_billing ? <span className="badge bg-secondary">Bill</span> : null}
                    {!r.is_default_shipping && !r.is_default_billing ? '—' : null}
                  </td>
                  <td className="text-muted small">{fmtTime(r.created_at)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <CiPaginationBar
        position="bottom"
        page={page}
        totalPages={totalPages}
        total={total}
        loading={loading}
        onPrevPage={onPrevPage}
        onNextPage={onNextPage}
      />
    </div>
  );
}
